import React from 'react';
import { ArrowRight, Sparkles, Star, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, housekeepingImages } from '../data/mock';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden bg-shsNavy">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&h=1080&fit=crop"
          alt="Luxury Hotel"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-shsNavy via-shsNavy/90 to-shsNavy/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-shsNavy via-transparent to-shsNavy/30" />
      </div>

      {/* Premium Glow Effects */}
      <div className="absolute top-20 right-20 w-[400px] h-[400px] bg-shsTeal/20 rounded-full blur-[120px]" />
      <div className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-shsGold/10 rounded-full blur-[150px]" />
      
      {/* Animated Particles */}
      <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-shsGold rounded-full animate-pulse shadow-lg shadow-shsGold/50" />
      <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-shsTeal rounded-full animate-pulse delay-300 shadow-lg shadow-shsTeal/50" />
      <div className="absolute bottom-1/3 right-1/2 w-1 h-1 bg-white rounded-full animate-pulse delay-500" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center w-full">
          {/* Content */}
          <div className="space-y-10">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 shadow-xl">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-shsGold fill-shsGold" />
                ))}
              </div>
              <div className="w-px h-4 bg-white/30" />
              <span className="text-white/90 text-sm font-medium">Trusted by 10+ Premium Hotels</span>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                Premium
                <span className="block relative">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-shsTeal via-emerald-400 to-cyan-400">
                    Housekeeping
                  </span>
                  <div className="absolute -bottom-2 left-0 w-1/2 h-1 bg-gradient-to-r from-shsGold to-transparent rounded-full" />
                </span>
                <span className="block text-white/95">Services</span>
              </h1>
            </div>

            {/* Tagline */}
            <p className="text-xl lg:text-2xl text-white/70 max-w-lg leading-relaxed font-light">
              Delivering <span className="text-shsGold font-semibold">5-star room presentation</span> through 
              professionally trained housekeeping teams across Australia.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Sparkles, text: 'Immaculate Standards' },
                { icon: Shield, text: 'Vetted Professionals' },
                { icon: Clock, text: '24/7 Support' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-5 py-3 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 hover:bg-white/10 hover:border-shsTeal/50 transition-all duration-300 group cursor-pointer"
                >
                  <item.icon className="w-4 h-4 text-shsTeal group-hover:text-shsGold transition-colors" />
                  <span className="text-white/90 text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={() => scrollToSection('#contact')}
                className="bg-gradient-to-r from-shsTeal to-emerald-500 hover:from-emerald-500 hover:to-shsTeal text-white px-10 py-8 rounded-full text-lg font-bold transition-all duration-500 hover:shadow-2xl hover:shadow-shsTeal/40 hover:scale-105 group"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                onClick={() => scrollToSection('#services')}
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white hover:text-shsNavy px-10 py-8 rounded-full text-lg font-semibold transition-all duration-300 backdrop-blur-sm hover:shadow-xl"
              >
                View Services
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8 lg:gap-12 pt-8 border-t border-white/10">
              <div className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-shsGold to-amber-400">200+</p>
                <p className="text-white/50 text-sm mt-1">Staff Placed</p>
              </div>
              <div className="w-px h-14 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
              <div className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-shsGold to-amber-400">10+</p>
                <p className="text-white/50 text-sm mt-1">Hotel Partners</p>
              </div>
              <div className="w-px h-14 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
              <div className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-shsGold to-amber-400">98%</p>
                <p className="text-white/50 text-sm mt-1">Satisfaction</p>
              </div>
            </div>
          </div>

          {/* Right Side - Clean Image Grid */}
          <div className="relative hidden lg:block">
            {/* Main Container */}
            <div className="relative bg-white/10 backdrop-blur-sm rounded-[40px] p-6 border border-white/20">
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-shsTeal/30 rounded-full blur-[80px]" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-shsGold/20 rounded-full blur-[60px]" />
              
              {/* Image Grid */}
              <div className="relative grid grid-cols-2 gap-4">
                {/* Main Large Image */}
                <div className="col-span-2 rounded-3xl overflow-hidden h-72">
                  <img
                    src={housekeepingImages.making_bed_1}
                    alt="Professional Housekeeping"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                
                {/* Two Smaller Images */}
                <div className="rounded-2xl overflow-hidden h-44">
                  <img
                    src={housekeepingImages.making_bed_2}
                    alt="Bed Making"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-44">
                  <img
                    src={housekeepingImages.cleaning_room}
                    alt="Room Cleaning"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Stats Bar */}
              <div className="relative mt-5 flex items-center justify-between bg-shsNavy/80 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="text-center flex-1">
                  <p className="text-2xl font-bold text-shsGold">5★</p>
                  <p className="text-white/60 text-xs mt-1">Service</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center flex-1">
                  <p className="text-2xl font-bold text-shsGold">15+</p>
                  <p className="text-white/60 text-xs mt-1">Years</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center flex-1">
                  <p className="text-2xl font-bold text-shsGold">24/7</p>
                  <p className="text-white/60 text-xs mt-1">Support</p>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-shsGold to-amber-500 text-shsNavy px-5 py-3 rounded-2xl font-bold shadow-lg shadow-shsGold/30">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span>Premium Quality</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
